import { SectionPlayer } from './section-player';
import { createSilentWav } from './silence-generator';

export type PlaylistState = 'idle' | 'loading' | 'playing' | 'paused';

export interface PlaylistSection {
  url: string | null;
  durationMs: number;
}

interface PlaylistOptions {
  crossfadeDurationMs?: number;
}

const DEFAULT_CROSSFADE_MS = 500;

export class PlaylistManager {
  private sections: PlaylistSection[];
  private crossfadeDurationMs: number;
  private globalStartMs: number[];
  private _totalDurationMs: number;

  private playerA: SectionPlayer;
  private playerB: SectionPlayer;
  private elementA: HTMLAudioElement;
  private elementB: HTMLAudioElement;
  private activePlayer: SectionPlayer;
  private activeElement: HTMLAudioElement;
  private nextPlayer: SectionPlayer;
  private nextElement: HTMLAudioElement;

  private _state: PlaylistState = 'idle';
  private _currentSectionIndex = 0;
  private _isPlaying = false;
  private _playbackRate = 1.0;
  private _masterVolume = 1.0;

  private rafId: number | null = null;
  private crossfading = false;
  private crossfadeStartTime: number | null = null;
  private nextSectionPreloaded = false;
  private nextSectionIndex: number | null = null;

  private stateChangeCallbacks = new Set<(state: PlaylistState) => void>();
  private timeUpdateCallbacks = new Set<(timeMs: number) => void>();
  private sectionChangeCallbacks = new Set<(index: number) => void>();

  private blobUrls: string[] = [];
  private sectionBlobUrls = new Map<number, string>();

  constructor(sections: PlaylistSection[], options?: PlaylistOptions) {
    this.sections = [...sections];
    this.crossfadeDurationMs = options?.crossfadeDurationMs ?? DEFAULT_CROSSFADE_MS;

    // Precompute global start times
    this.globalStartMs = [];
    let cumulative = 0;
    for (const section of this.sections) {
      this.globalStartMs.push(cumulative);
      cumulative += section.durationMs;
    }
    this._totalDurationMs = cumulative;

    // Create blob URLs for null-url sections
    for (let i = 0; i < this.sections.length; i++) {
      if (this.sections[i].url === null) {
        const blob = createSilentWav(this.sections[i].durationMs);
        const blobUrl = URL.createObjectURL(blob);
        this.blobUrls.push(blobUrl);
        this.sectionBlobUrls.set(i, blobUrl);
      }
    }

    // Create dual audio elements and players
    this.elementA = document.createElement('audio') as HTMLAudioElement;
    this.elementB = document.createElement('audio') as HTMLAudioElement;
    this.playerA = new SectionPlayer(this.elementA);
    this.playerB = new SectionPlayer(this.elementB);

    this.activePlayer = this.playerA;
    this.activeElement = this.elementA;
    this.nextPlayer = this.playerB;
    this.nextElement = this.elementB;
  }

  // --- Public API ---

  async play(): Promise<void> {
    if (this._state === 'playing') return;

    if (this._state === 'idle') {
      this.setState('loading');
      const url = this.getUrlForSection(this._currentSectionIndex);
      await this.activePlayer.load(url);
      this.setupEndedHandler(this.activePlayer, this._currentSectionIndex);
      this.activePlayer.volume = this._masterVolume;
      this.setState('playing');
      this.activePlayer.play();
      this._isPlaying = true;
      this.startRafLoop();
      return;
    }

    if (this._state === 'paused') {
      this.activePlayer.play();
      this._isPlaying = true;
      this.setState('playing');
      this.startRafLoop();
      return;
    }

    // If loading, do nothing (wait for current load)
  }

  pause(): void {
    if (this._state !== 'playing' && this._state !== 'loading') return;

    this.activePlayer.pause();
    this._isPlaying = false;
    this.stopRafLoop();

    if (this._state === 'playing') {
      this.setState('paused');
    }
  }

  seek(globalTimeMs: number): void {
    const clamped = Math.max(0, Math.min(globalTimeMs, this._totalDurationMs - 1));
    const { index, offsetMs } = this.findSectionAtTime(clamped);

    if (index === this._currentSectionIndex) {
      // Seek within current section
      this.activePlayer.currentTime = offsetMs;
      this.crossfading = false;
      this.nextSectionPreloaded = false;
      return;
    }

    // Seek to different section
    const wasPlaying = this._isPlaying;
    this.crossfading = false;
    this.nextSectionPreloaded = false;

    this.activePlayer.stop();
    this.nextPlayer.stop();

    this._currentSectionIndex = index;
    this.emitSectionChange(index);

    const url = this.getUrlForSection(index);
    this.setState('loading');

    this.activePlayer.load(url).then(() => {
      this.setupEndedHandler(this.activePlayer, index);
      this.activePlayer.currentTime = offsetMs;
      if (wasPlaying) {
        this.activePlayer.play();
        this._isPlaying = true;
        this.setState('playing');
        this.startRafLoop();
      } else {
        this.setState('paused');
      }
    });
  }

  get currentTimeMs(): number {
    if (this.sections.length === 0) return 0;
    const sectionStart = this.globalStartMs[this._currentSectionIndex] ?? 0;
    return sectionStart + this.activePlayer.currentTime;
  }

  get totalDurationMs(): number {
    return this._totalDurationMs;
  }

  get isPlaying(): boolean {
    return this._isPlaying;
  }

  get state(): PlaylistState {
    return this._state;
  }

  get currentSectionIndex(): number {
    return this._currentSectionIndex;
  }

  get sectionProgress(): number {
    if (this.sections.length === 0) return 0;
    const sectionDurationMs =
      this.activePlayer.duration || this.sections[this._currentSectionIndex]?.durationMs || 0;
    if (sectionDurationMs <= 0) return 0;
    return Math.min(this.activePlayer.currentTime / sectionDurationMs, 1);
  }

  setPlaybackRate(rate: number): void {
    this._playbackRate = Math.max(0.9, Math.min(1.1, rate));
    this.activePlayer.playbackRate = this._playbackRate;
    if (this.crossfading) {
      this.nextPlayer.playbackRate = this._playbackRate;
    }
  }

  setVolume(volume: number): void {
    this._masterVolume = Math.max(0, Math.min(1, volume));
    if (!this.crossfading) {
      this.activePlayer.volume = this._masterVolume;
    }
  }

  seekToSection(index: number): void {
    if (index < 0 || index >= this.sections.length) return;
    this._playbackRate = 1.0;
    this.activePlayer.playbackRate = 1.0;
    if (this.crossfading) {
      this.nextPlayer.playbackRate = 1.0;
    }
    this.seek(this.globalStartMs[index]);
  }

  getAudioElements(): [HTMLAudioElement, HTMLAudioElement] {
    return [this.elementA, this.elementB];
  }

  // --- Events ---

  onStateChange(cb: (state: PlaylistState) => void): () => void {
    this.stateChangeCallbacks.add(cb);
    return () => this.stateChangeCallbacks.delete(cb);
  }

  onTimeUpdate(cb: (timeMs: number) => void): () => void {
    this.timeUpdateCallbacks.add(cb);
    return () => this.timeUpdateCallbacks.delete(cb);
  }

  onSectionChange(cb: (index: number) => void): () => void {
    this.sectionChangeCallbacks.add(cb);
    return () => this.sectionChangeCallbacks.delete(cb);
  }

  // --- Lifecycle ---

  updateUrls(sections: PlaylistSection[]): void {
    // Revoke old silence blob URLs
    for (const url of this.blobUrls) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls = [];
    this.sectionBlobUrls.clear();

    this.sections = [...sections];

    // Recompute global start times
    this.globalStartMs = [];
    let cumulative = 0;
    for (const section of this.sections) {
      this.globalStartMs.push(cumulative);
      cumulative += section.durationMs;
    }
    this._totalDurationMs = cumulative;

    // Recreate blob URLs for null sections
    for (let i = 0; i < this.sections.length; i++) {
      if (this.sections[i].url === null) {
        const blob = createSilentWav(this.sections[i].durationMs);
        const blobUrl = URL.createObjectURL(blob);
        this.blobUrls.push(blobUrl);
        this.sectionBlobUrls.set(i, blobUrl);
      }
    }
  }

  destroy(): void {
    this.stopRafLoop();
    this.playerA.destroy();
    this.playerB.destroy();
    this._state = 'idle';
    this._isPlaying = false;
    this.crossfading = false;

    // Revoke blob URLs
    for (const url of this.blobUrls) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls = [];
    this.sectionBlobUrls.clear();

    this.stateChangeCallbacks.clear();
    this.timeUpdateCallbacks.clear();
    this.sectionChangeCallbacks.clear();
  }

  // --- Private ---

  private getUrlForSection(index: number): string {
    const blobUrl = this.sectionBlobUrls.get(index);
    if (blobUrl) return blobUrl;
    return this.sections[index].url!;
  }

  private setState(newState: PlaylistState): void {
    this._state = newState;
    for (const cb of this.stateChangeCallbacks) {
      cb(newState);
    }
  }

  private emitSectionChange(index: number): void {
    for (const cb of this.sectionChangeCallbacks) {
      cb(index);
    }
  }

  private emitTimeUpdate(timeMs: number): void {
    for (const cb of this.timeUpdateCallbacks) {
      cb(timeMs);
    }
  }

  private setupEndedHandler(player: SectionPlayer, sectionIndex: number): void {
    player.onEnded(() => {
      if (this.crossfading) return; // crossfade handles transition
      this.advanceToNextSection(sectionIndex);
    });
  }

  private advanceToNextSection(fromIndex: number): void {
    const nextIndex = fromIndex + 1;
    if (nextIndex >= this.sections.length) {
      // Playlist ended
      this._isPlaying = false;
      this.stopRafLoop();
      this.setState('paused');
      return;
    }

    this._currentSectionIndex = nextIndex;
    this.emitSectionChange(nextIndex);
    this.crossfading = false;
    this.nextSectionPreloaded = false;

    // Swap players
    this.swapPlayers();

    const url = this.getUrlForSection(nextIndex);
    this.setState('loading');

    this.activePlayer.load(url).then(() => {
      this.setupEndedHandler(this.activePlayer, nextIndex);
      this.activePlayer.playbackRate = this._playbackRate;
      this.activePlayer.play();
      this._isPlaying = true;
      this.setState('playing');
      this.startRafLoop();
    });
  }

  private swapPlayers(): void {
    const tmpPlayer = this.activePlayer;
    const tmpElement = this.activeElement;
    this.activePlayer = this.nextPlayer;
    this.activeElement = this.nextElement;
    this.nextPlayer = tmpPlayer;
    this.nextElement = tmpElement;
  }

  private startRafLoop(): void {
    this.stopRafLoop();
    const tick = () => {
      if (!this._isPlaying) return;

      this.emitTimeUpdate(this.currentTimeMs);
      this.checkCrossfade();

      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private stopRafLoop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private checkCrossfade(): void {
    if (this.crossfading) {
      this.performCrossfade();
      return;
    }

    const currentDurationMs =
      this.activePlayer.duration || this.sections[this._currentSectionIndex]?.durationMs || 0;
    const currentTimeMs = this.activePlayer.currentTime;
    const remainingMs = currentDurationMs - currentTimeMs;

    // If we're within crossfade duration of end and there's a next section
    if (
      remainingMs <= this.crossfadeDurationMs &&
      remainingMs > 0 &&
      this._currentSectionIndex + 1 < this.sections.length &&
      !this.nextSectionPreloaded
    ) {
      this.startCrossfade();
    }
  }

  private startCrossfade(): void {
    const nextIndex = this._currentSectionIndex + 1;
    this.nextSectionIndex = nextIndex;
    this.nextSectionPreloaded = true;

    const url = this.getUrlForSection(nextIndex);
    this.nextPlayer.load(url).then(() => {
      this.setupEndedHandler(this.nextPlayer, nextIndex);
      this.nextPlayer.volume = 0;
      this.nextPlayer.playbackRate = this._playbackRate;
      this.nextPlayer.play();
      this.crossfading = true;
      this.crossfadeStartTime = performance.now();
    });
  }

  private performCrossfade(): void {
    if (this.crossfadeStartTime === null) return;

    const elapsed = performance.now() - this.crossfadeStartTime;
    const progress = Math.min(elapsed / this.crossfadeDurationMs, 1);

    // Linear crossfade: current fades out, next fades in (scaled by master volume)
    this.activePlayer.volume = (1 - progress) * this._masterVolume;
    this.nextPlayer.volume = progress * this._masterVolume;

    if (progress >= 1) {
      // Crossfade complete
      this.activePlayer.stop();
      this.activePlayer.volume = this._masterVolume;

      this.swapPlayers();

      this._currentSectionIndex = this.nextSectionIndex!;
      this.emitSectionChange(this._currentSectionIndex);

      this.crossfading = false;
      this.crossfadeStartTime = null;
      this.nextSectionPreloaded = false;
      this.nextSectionIndex = null;
    }
  }

  private findSectionAtTime(globalTimeMs: number): { index: number; offsetMs: number } {
    for (let i = this.sections.length - 1; i >= 0; i--) {
      if (globalTimeMs >= this.globalStartMs[i]) {
        return { index: i, offsetMs: globalTimeMs - this.globalStartMs[i] };
      }
    }
    /* v8 ignore next -- defensive fallback: seek() clamps input so this path is unreachable */
    return { index: 0, offsetMs: 0 };
  }
}
